import type { StandardServicePageContent } from "~/content/servicePage";

export const ChimneyInspectionContent: StandardServicePageContent = {
  heroTitle: "Przegląd kominiarski",
  quickFactsTitle: "Najważniejsze informacje w skrócie",
  quickFacts: [
    {
      label: "Jak często",
      value: "Minimum co rok",
      description:
        "Obowiązek dotyczy przewodów dymowych, spalinowych i wentylacyjnych. W praktyce kontrolę warto planować przed sezonem grzewczym albo po wymianie kotła, piecyka lub kominka.",
    },
    {
      label: "Ile kosztuje",
      value: "Najczęściej 150-350 zł w domu",
      description:
        "Cena zależy od liczby przewodów, dostępu do komina, typu budynku i miasta. Kamienice, budynki wielorodzinne i obiekty usługowe wycenia się zwykle indywidualnie.",
    },
    {
      label: "Co obejmuje",
      value: "Drożność, ciąg i protokół",
      description:
        "Sprawdza się stan przewodów, czopuchów, nasad, wentylacji oraz warunki pracy urządzeń grzewczych. Po wizycie właściciel powinien dostać protokół albo e-protokół z zaleceniami.",
    },
    {
      label: "Kiedy działać wcześniej",
      value: "Po remoncie, wymianie urządzenia lub problemach z ciągiem",
      description:
        "Sygnałem są cofanie spalin, zapach dymu, słaba wentylacja w łazience i kuchni albo zadymienie przy kominku. To szczególnie ważne w starszych budynkach i lokalach z piecykiem gazowym.",
    },
  ],
  faqTitle: "Najczęstsze pytania o przegląd kominiarski",
  sections: [
    {
      title: "Na czym polega przegląd kominiarski i co obejmuje",
      paragraphs: [
        `Przegląd kominiarski to kontrola przewodów dymowych, spalinowych i wentylacyjnych pod kątem drożności, ciągu oraz bezpiecznego użytkowania budynku. Chodzi nie tylko o sam komin ponad dachem, ale o cały układ odprowadzania spalin i wentylacji.`,
        `W praktyce sprawdza się stan przewodów, czopuchów, nasad, wyczystek, dostęp do czyszczenia oraz warunki pracy urządzeń grzewczych. Dobra kontrola pozwala wcześniej wykryć ryzyko cofania spalin, pożaru sadzy albo nieskutecznej wentylacji w pomieszczeniach.`,
        `Po wizycie właściciel lub zarządca powinien wiedzieć, czy instalacja kominowa nadaje się do dalszego użytkowania, jakie usterki trzeba usunąć i czy potrzebne jest dodatkowe czyszczenie albo naprawa przewodów.`,
      ],
    },
    {
      title: "Obowiązkowy przegląd kominiarski co rok i czym różni się od czyszczenia",
      paragraphs: [
        `Roczny przegląd kominiarski wynika z art. 62 ust. 1 pkt 1 lit. c Prawa budowlanego i dotyczy budynków, w których występują przewody kominowe. Obowiązek obejmuje zarówno domy jednorodzinne, jak i mieszkania, wspólnoty oraz obiekty usługowe.`,
        `Warto odróżnić przegląd od czyszczenia przewodów. Kontrola ma ocenić stan techniczny i bezpieczeństwo, natomiast czyszczenie usuwa sadzę i osady z częstotliwością zależną od rodzaju paliwa oraz sposobu użytkowania urządzeń.`,
        `W praktyce aktualny protokół ma znaczenie nie tylko przy wypełnianiu obowiązku ustawowego. Przydaje się też przy ubezpieczeniu nieruchomości, zarządzaniu budynkiem i wyjaśnianiu przyczyn problemów z wentylacją albo ciągiem kominowym.`,
      ],
    },
    {
      title: "Przegląd kominiarski w domu, mieszkaniu i budynku wspólnoty",
      paragraphs: [
        `W domu jednorodzinnym kontrola obejmuje zwykle przewody obsługujące kocioł, kominek, piec lub podgrzewacz wody, a także kanały wentylacyjne. W mieszkaniu znaczenie ma zarówno lokalny układ wentylacji, jak i przewody wspólne obsługujące cały budynek.`,
        `W blokach, kamienicach i budynkach wspólnot rośnie znaczenie organizacji wizyty oraz dokumentacji dla części wspólnych. Dla mieszkańca najważniejsze jest jednak to samo: potwierdzenie, że spaliny są odprowadzane prawidłowo, a wentylacja nie stwarza zagrożenia dla użytkowników.`,
        `To właśnie w starszych budynkach najczęściej wychodzą problemy z nieszczelnościami, zaburzeniami ciągu po remontach, nieprawidłowymi podłączeniami urządzeń albo zmianami w układzie wentylacji. Dlatego zakres oględzin powinien być dopasowany do wieku obiektu i sposobu jego użytkowania.`,
      ],
    },
    {
      title: "Cena przeglądu kominiarskiego i od czego zależy",
      paragraphs: [
        `Cena przeglądu kominiarskiego w domu jednorodzinnym wynosi najczęściej około 150-350 zł. W większych budynkach, kamienicach i obiektach usługowych wycena zależy przede wszystkim od liczby przewodów, kondygnacji i zakresu dokumentacji.`,
        `Na koszt wpływa też dostęp do kominów, stan techniczny budynku oraz to, czy wizyta dotyczy standardowej kontroli okresowej, czy sprawy pilnej po problemach z ciągiem, zadymieniu albo zmianie urządzenia grzewczego. W większych miastach cena bywa wyższa również ze względu na dojazd i ograniczoną dostępność terminów.`,
        `Porównując oferty, warto sprawdzić, czy cena obejmuje samą wizytę, czy również protokół, ocenę wentylacji i jasne zalecenia po kontroli. To zakres odpowiedzialności wykonawcy zwykle najlepiej pokazuje realną wartość usługi.`,
      ],
    },
  ],
  scopeTitle: "Zakres usług przeglądu kominiarskiego",
  scopeItems: [
    "Oględziny przewodów dymowych",
    "Kontrola przewodów spalinowych",
    "Ocena kanałów wentylacyjnych",
    "Sprawdzenie ciągu kominowego",
    "Kontrola czopuchów i podłączeń",
    "Ocena nasad i wyczystek",
    "Weryfikacja dostępu do czyszczenia",
    "Protokół lub e-protokół kominiarski",
  ],
  seo: {
    title: "Przegląd kominiarski co rok: cena, protokół, co obejmuje",
    description:
      "Przegląd kominiarski dla domu, mieszkania i wspólnoty. Sprawdź, co obejmuje roczna kontrola, ile kosztuje, co zawiera protokół i kiedy zlecić wizytę wcześniej.",
  },
};
